// src/components/SolicitudTraslado.jsx
import React, { useEffect, useState } from "react";
import "./CSS/SolicitudTraslado.css";
import { dominio } from "../../../routes_backend/routes.jsx";


const API_URL = `${dominio}/SGIM_SolicitudTraslado`;
const TRANSFER_URL = `${dominio}/SGIM_TransferenciaStock`;
const API_KEY = "b4ed47a3-b372-49df-a200-2e68d5f7a454";

// Construye el JSON que tu backend espera a partir de la solicitud
const buildTransferFromSolicitud = (solicitud) => {
  // 1) Fecha: usamos la de SAP si viene, si no, hoy (formato YYYY-MM-DD)
  let docDate;
  try {
    if (solicitud.DocDate) {
      const d = new Date(solicitud.DocDate);
      if (!isNaN(d.getTime())) {
        docDate = d.toISOString().slice(0, 10);
      }
    }
  } catch (e) {
    docDate = null;
  }
  if (!docDate) {
    docDate = new Date().toISOString().slice(0, 10);
  }

  // 2) FromWarehouse y ToWarehouse (ajusta según tus campos reales del encabezado)
  const fromWarehouse = solicitud.Filler || ""; // En SAP suele ser bodega origen
  const toWarehouse = solicitud.ToWhsCode || solicitud.WhsCode || "";

  // 3) Encabezado en el formato que probaste en Postman
  const transferencia = {
    DocDate: docDate,
    DueDate: docDate,
    FromWarehouse: fromWarehouse,
    ToWarehouse: toWarehouse,
    Comments:
      solicitud.Comments ||
      `Traslado basado en solicitud ${solicitud.DocNum || solicitud.DocEntry}`,
    StockTransferLines: [],
  };

  // 4) Líneas (mapeando desde DocumentLines)
  if (Array.isArray(solicitud.DocumentLines)) {
    transferencia.StockTransferLines = solicitud.DocumentLines.map(
      (linea, idx) => ({
        ItemCode: linea.ItemCode,
        Quantity: linea.Quantity,
        FromWarehouseCode: fromWarehouse, // origen
        WarehouseCode: toWarehouse || linea.WhsCode, // destino

        // Amarre del documento base (Solicitud de traslado OWTQ)
        // BaseType 1250000001 suele ser "Inventory Transfer Request" (OWTQ)
        BaseType: 1250000001,
        BaseEntry: solicitud.DocEntry, // DocEntry de la solicitud
        BaseLine:
          linea.LineNum !== undefined && linea.LineNum !== null
            ? linea.LineNum
            : idx,
      })
    );
  }

  return transferencia;
};

const SolicitudTraslado = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [openedDocEntry, setOpenedDocEntry] = useState(null);

  const [submittingDocEntry, setSubmittingDocEntry] = useState(null);
  const [transferAlert, setTransferAlert] = useState(null); // {type: 'success'|'error', message: ''}

  // 🔁 Función reutilizable para cargar las solicitudes
  const fetchSolicitudes = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(API_URL, {
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.answer !== "true") {
        throw new Error(data.message || "Error al obtener las solicitudes");
      }

      setSolicitudes(data.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Se ejecuta solo al montar el componente
  useEffect(() => {
    fetchSolicitudes();
  }, []);

  const handleToggleLines = (docEntry) => {
    setOpenedDocEntry((prev) => (prev === docEntry ? null : docEntry));
  };

  const handleTransfer = async (solicitud, e) => {
    if (e) e.stopPropagation();

    setTransferAlert(null);
    setSubmittingDocEntry(solicitud.DocEntry);

    try {
      // 👉 Aquí armamos el JSON EXACTO que tu backend espera
      const payload = buildTransferFromSolicitud(solicitud);
      console.log("JSON enviado a SGIM_TransferenciaStock:", payload);

      const res = await fetch(TRANSFER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.answer !== "true") {
        throw new Error(data.message || `Error ${res.status}`);
      }

      setTransferAlert({
        type: "success",
        message:
          data.message ||
          `Transferencia realizada exitosamente para la solicitud ${
            solicitud.DocNum || solicitud.DocEntry
          }.`,
      });

      // 🔁 Recargar el listado de solicitudes desde el backend
      await fetchSolicitudes();
    } catch (err) {
      console.error(err);
      setTransferAlert({
        type: "error",
        message: err.message || "Error al realizar la transferencia",
      });
    } finally {
      setSubmittingDocEntry(null);
    }
  };

  const filteredSolicitudes = solicitudes.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();

    const docEntry = String(s.DocEntry || "").toLowerCase();
    const docNum = String(s.DocNum || "").toLowerCase();
    const comments = String(s.Comments || "").toLowerCase();
    const destino = String(s.ToWhsCode || s.Filler || "").toLowerCase();

    const coincideArticulo =
      Array.isArray(s.DocumentLines) &&
      s.DocumentLines.some((linea) => {
        const itemCode = String(linea.ItemCode || "").toLowerCase();
        const desc = String(linea.Dscription || "").toLowerCase();
        return itemCode.includes(q) || desc.includes(q);
      });

    return (
      docEntry.includes(q) ||
      docNum.includes(q) ||
      comments.includes(q) ||
      destino.includes(q) ||
      coincideArticulo
    );
  });

  return (
    <div className="solicitud-traslado-container">
      <header className="solicitud-traslado-header">
        <div>
          <h1>Solicitudes de Traslado</h1>
          <p>Listado de solicitudes abiertas en SAP B1</p>
        </div>

        <div className="solicitud-traslado-actions">
          <input
            type="text"
            placeholder="Buscar por DocEntry, DocNum, comentario, bodega o artículo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </header>

      {transferAlert && (
        <div
          className={`solicitud-traslado-status ${
            transferAlert.type === "success"
              ? "status-success"
              : "status-error"
          }`}
        >
          {transferAlert.message}
        </div>
      )}

      {loading && (
        <div className="solicitud-traslado-status status-loading">
          Cargando solicitudes...
        </div>
      )}

      {error && (
        <div className="solicitud-traslado-status status-error">
          Ocurrió un error: {error}
        </div>
      )}

      {!loading && !error && filteredSolicitudes.length === 0 && (
        <div className="solicitud-traslado-status status-empty">
          No hay solicitudes que coincidan con el filtro.
        </div>
      )}

      <section className="solicitud-traslado-list">
        {filteredSolicitudes.map((solicitud) => {
          const docEntry = solicitud.DocEntry;
          const isOpen = openedDocEntry === docEntry;
          const isSubmitting = submittingDocEntry === docEntry;

          return (
            <article
              key={docEntry}
              className={`solicitud-card ${isOpen ? "open" : ""}`}
            >
              <div
                className="solicitud-card-header"
                onClick={() => handleToggleLines(docEntry)}
              >
                <div className="solicitud-card-main">
                  <h2>Solicitud #{solicitud.DocNum || docEntry}</h2>
                  <p className="solicitud-card-subtitle">
                    De: <strong>{solicitud.Filler || "N/D"}</strong> &nbsp;→&nbsp;
                    Hacia:{" "}
                    <strong>
                      {solicitud.ToWhsCode || solicitud.WhsCode || "N/D"}
                    </strong>
                  </p>
                  {solicitud.Comments && (
                    <p className="solicitud-card-comments">
                      {solicitud.Comments}
                    </p>
                  )}
                </div>

                <div className="solicitud-card-meta">
                  <span className="solicitud-pill solicitud-pill-open">
                    Abierta
                  </span>
                  <span className="solicitud-date">
                    {solicitud.DocDate
                      ? new Date(solicitud.DocDate).toLocaleDateString()
                      : "Sin fecha"}
                  </span>

                  <button
                    type="button"
                    className="solicitud-toggle-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleLines(docEntry);
                    }}
                  >
                    {isOpen ? "Ocultar líneas" : "Ver líneas"}
                  </button>

                  <button
                    type="button"
                    className="solicitud-transfer-btn"
                    disabled={isSubmitting}
                    onClick={(e) => handleTransfer(solicitud, e)}
                  >
                    {isSubmitting ? "Procesando..." : "Realizar transferencia"}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="solicitud-card-body">
                  <div className="lines-table">
                    <div className="lines-header">
                      <span>Código</span>
                      <span>Descripción</span>
                      <span>Cant.</span>
                      <span>Bodega</span>
                      <span>Precio</span>
                    </div>
                    {Array.isArray(solicitud.DocumentLines) &&
                      solicitud.DocumentLines.map((linea, index) => (
                        <div key={index} className="lines-row">
                          <span>{linea.ItemCode}</span>
                          <span>{linea.Dscription}</span>
                          <span>{linea.Quantity}</span>
                          <span>{linea.WhsCode}</span>
                          <span>
                            {linea.Price != null
                              ? Number(linea.Price).toLocaleString("es-GT", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : "-"}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </section>
    </div>
  );
};

export default SolicitudTraslado;
